/**
 * DYNAMIC BLOG PAGE
 * Fetches blog posts from shared backend
 * Updates automatically across all sites
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useSite } from '../contexts/SiteContext';
import { getBlogPosts } from '../services/api';
import { ArrowRight, Search, Calendar, User, Tag } from 'lucide-react';

// Fallback blog posts if API fails
const fallbackPosts = [
  {
    id: '1',
    title: 'Understanding Rotator Cuff Tears',
    slug: 'understanding-rotator-cuff-tears',
    excerpt: 'Learn about the common causes of rotator cuff tears and treatment options.',
    author: 'Dr. Deepthi Nandan Reddy',
    category: 'shoulder',
    tags: ['rotator cuff', 'shoulder pain'],
    image_url: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800',
    created_at: new Date().toISOString()
  },
  {
    id: '2',
    title: 'Tennis Elbow: Prevention and Recovery',
    slug: 'tennis-elbow-prevention-recovery',
    excerpt: 'Everything you need to know about lateral epicondylitis.',
    author: 'Dr. Deepthi Nandan Reddy',
    category: 'elbow',
    tags: ['tennis elbow', 'sports injury'],
    image_url: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800',
    created_at: new Date().toISOString()
  },
  {
    id: '3',
    title: 'Carpal Tunnel Syndrome: Signs and Solutions',
    slug: 'carpal-tunnel-syndrome-signs-solutions',
    excerpt: 'Recognize early signs and explore treatment options.',
    author: 'Dr. Deepthi Nandan Reddy',
    category: 'hand-wrist',
    tags: ['carpal tunnel', 'wrist pain'],
    image_url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800',
    created_at: new Date().toISOString()
  }
];

export const Blog = () => {
  const { theme, bodyPart, id: siteId } = useSite();
  const [posts, setPosts] = useState(fallbackPosts);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'shoulder', 'elbow', 'hand-wrist', 'general'];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Map site IDs to blog categories
        const categoryMap = {
          'shoulder': 'shoulder',
          'elbow': 'elbow',
          'handandwrist': 'hand-wrist',
          'main': null
        };
        // For specialty sites, filter by category. For main site, show all.
        const categoryFilter = siteId !== 'main' ? categoryMap[siteId] : null;
        const data = await getBlogPosts(categoryFilter);
        if (data && data.length > 0) {
          setPosts(data);
        }
      } catch (error) {
        console.log('Using fallback blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [siteId]);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           post.category?.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" data-testid="blog-page">
      {/* Hero */}
      <section className="py-16" style={{ backgroundColor: theme.lightBgHex }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {bodyPart} Care Blog
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Expert insights, tips, and advice for {bodyPart.toLowerCase()} health
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="blog-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Show all categories on main site */}
      {siteId === 'main' && (
        <section className="py-8 border-b bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category)}
                  style={selectedCategory === category ? { backgroundColor: theme.primaryHex } : {}}
                  className={selectedCategory === category ? 'text-white' : ''}
                >
                  {category === 'hand-wrist' ? 'Hand & Wrist' : category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <Card 
                    key={post.id || index} 
                    className="overflow-hidden hover:shadow-lg transition-shadow"
                    data-testid={`blog-post-${index}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800'}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: theme.primaryHex }}
                        >
                          {post.category === 'hand-wrist' ? 'Hand & Wrist' : post.category?.charAt(0).toUpperCase() + post.category?.slice(1)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                      <CardTitle className="text-xl line-clamp-2">{post.title}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {post.excerpt}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {post.author}
                        </span>
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="font-medium flex items-center gap-1 hover:gap-2 transition-all"
                          style={{ color: theme.primaryHex }}
                        >
                          Read More <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">No articles found matching your criteria.</p>
                  <Button 
                    onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                    style={{ backgroundColor: theme.primaryHex }}
                    className="text-white"
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16" style={{ backgroundColor: theme.primaryHex }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Stay Updated on {bodyPart} Health
          </h2>
          <p className="text-white/80 mb-6 max-w-2xl mx-auto">
            Get the latest articles, tips, and expert advice delivered to your inbox.
          </p>
          <div className="max-w-md mx-auto flex gap-2">
            <Input 
              type="email" 
              placeholder="Enter your email"
              className="bg-white"
            />
            <Button className="bg-white hover:bg-gray-100" style={{ color: theme.primaryHex }}>
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
